/* One controller for 1–3 Shopify-hosted videos, still covers, and mixed scenes. */
(() => {
  const instances = new Map();
  const preference = window.matchMedia('(prefers-reduced-motion: reduce)');
  function setup(root) {
    if (instances.has(root)) return;
    const scenes = [...root.querySelectorAll('[data-banner-scene]')];
    const controls = root.querySelector('[data-banner-controls]');
    if (!scenes.length || !controls) return;
    const videos = scenes.map(scene => scene.querySelector('[data-living-video]'));
    const dots = [...controls.querySelectorAll('[data-scene-select]')];
    const toggle = controls.querySelector('[data-scene-toggle]');
    const caption = controls.querySelector('[data-scene-caption]');
    const labels = dots.map(dot => dot.getAttribute('aria-label').replace(/^Show scene \d+: /, ''));
    const hold = Math.max(5, Math.min(15, Number(root.dataset.interval) || 6)) * 1000;
    const fade = Math.max(150, Math.min(500, Number(root.dataset.fade) || 250));
    const dataSaving = Boolean(navigator.connection?.saveData);
    const connection = navigator.connection;
    // Network Information API values are absent or conservative in many desktop
    // browsers. Use viewport size for image quality and keep bandwidth savings in
    // the next-scene preloader instead of making a large desktop banner blurry.
    // Keep full 1080p quality on desktop; preload the active video to improve startup.
    const allowHd = window.innerWidth >= 900;
    const abort = new AbortController();
    const listen = (target, event, fn) => target.addEventListener(event, fn, {signal: abort.signal});
    const posters = scenes.map(() => null), prepared = scenes.map(() => null), bad = scenes.map(() => false);
    let current = 0, incoming = null, animation = null, timer, warmTimer;
    let serial = 0, syncSerial = 0, cycles = 0, switching = false, destroyed = false;
    const bounds = root.getBoundingClientRect();
    let visible = bounds.bottom > 0 && bounds.top < window.innerHeight;
    // Muted Shopify-hosted videos may autoplay in both the storefront and theme editor.
    // Reduced-motion and data-saver preferences still take priority.
    let paused = root.dataset.autoplay !== 'true' || preference.matches;
    const running = () => !destroyed && !paused && visible && !document.hidden;
    const nextIndex = () => (current + 1) % scenes.length;

    function preparePoster(index) {
      if (posters[index]) return posters[index];
      posters[index] = new Promise((resolve, reject) => {
        const img = scenes[index].querySelector('img');
        if (!img) {resolve(); return}
        let done = false, retried = false;
        const timeout = setTimeout(() => finish(new Error('Cover unavailable')), 15000);
        function finish(error) {
          if (done) return;
          done = true; clearTimeout(timeout);
          img.removeEventListener('load', loaded); img.removeEventListener('error', failed);
          abort.signal.removeEventListener('abort', cancelled);
          error ? reject(error) : resolve();
        }
        function loaded() {if (img.decode) img.decode().then(() => finish(), failed); else finish()}
        function failed() {
          if (retried || !img.dataset.posterFallback) {finish(new Error('Cover unavailable')); return}
          retried = true;
          scenes[index].querySelectorAll('picture source').forEach(source => source.removeAttribute('srcset'));
          img.removeAttribute('srcset'); img.src = img.dataset.posterFallback;
        }
        function cancelled() {finish(new Error('Section removed'))}
        img.addEventListener('load', loaded); img.addEventListener('error', failed);
        abort.signal.addEventListener('abort', cancelled, {once: true});
        scenes[index].querySelectorAll('[data-scene-srcset]').forEach(el => {el.srcset = el.dataset.sceneSrcset});
        if (img.dataset.sceneSrc) img.src = img.dataset.sceneSrc;
        if (img.complete && img.naturalWidth) loaded();
      });
      return posters[index];
    }

    function videoFallback(index) {
      bad[index] = true; videos[index]?.pause();
      scenes[index].classList.remove('is-video-ready');
      scenes[index].dataset.videoState = 'cover-fallback';
    }

    function prepareVideo(index) {
      if (!videos[index] || bad[index]) return Promise.resolve();
      if (prepared[index]) return prepared[index];
      prepared[index] = new Promise((resolve, reject) => {
        const video = videos[index];
        let done = false;
        const timeout = setTimeout(() => finish(new Error('Video load timeout')), 15000);
        function finish(error) {
          if (done) return;
          done = true; clearTimeout(timeout);
          video.removeEventListener('loadeddata', loaded); video.removeEventListener('error', failed);
          abort.signal.removeEventListener('abort', cancelled);
          error ? reject(error) : resolve();
        }
        function loaded() {
          if (video.readyState < 2 || !video.videoWidth) return;
          scenes[index].classList.add('is-video-ready');
          scenes[index].dataset.videoState = 'ready'; finish();
        }
        function failed() {finish(new Error('Video unavailable'))}
        function cancelled() {finish(new Error('Section removed'))}
        video.addEventListener('loadeddata', loaded); video.addEventListener('error', failed);
        abort.signal.addEventListener('abort', cancelled, {once: true});
        video.muted = true; video.defaultMuted = true; video.playsInline = true; video.playbackRate = 1;
        video.preload = index === current ? 'auto' : 'metadata';
        video.querySelectorAll('[data-living-src]').forEach(source => {
          let standard = source.dataset.livingSrc;
          let hd = source.dataset.livingSrcHd;
          const hls = source.dataset.livingSrcHls;
          const supportsHls = Boolean(hls && (video.canPlayType('application/vnd.apple.mpegurl') || video.canPlayType('application/x-mpegURL')));
          source.src = allowHd && hd ? hd : supportsHls ? hls : standard;
          source.type = supportsHls ? 'application/x-mpegURL' : 'video/mp4';
        });
        video.load(); loaded();
      }).catch(() => {if (!destroyed) videoFallback(index)});
      return prepared[index];
    }

    function bufferedAhead(video) {
      if (!video || !video.buffered.length) return 0;
      try { return Math.max(0, video.buffered.end(video.buffered.length - 1) - video.currentTime); }
      catch { return 0; }
    }

    function warmNextWhenSafe(selected, token) {
      if (destroyed || dataSaving || scenes.length < 2 || current !== selected || token !== syncSerial) return;
      const video = videos[selected];
      if (!video || bad[selected] || video.ended || bufferedAhead(video) >= Math.min(3, Math.max(1, video.duration - video.currentTime))) {
        prepare(nextIndex()).catch(() => {});
        return;
      }
      clearTimeout(warmTimer);
      warmTimer = setTimeout(() => warmNextWhenSafe(selected, token), 700);
    }

    async function prepare(index) {
      await Promise.all([preparePoster(index), prepareVideo(index)]);
    }

    function selection(index) {
      dots.forEach((dot, number) => {
        if (number === index) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
      caption.textContent = labels[index];
      scenes.forEach((scene, number) => {
        if (number === index) scene.removeAttribute('aria-hidden');
        else scene.setAttribute('aria-hidden', 'true');
      });
      root.dataset.activeScene = String(index + 1);
    }

    function resetVideo(index) {
      const video = videos[index];
      if (!video) return;
      video.pause();
      if (video.readyState >= 2) video.currentTime = 0;
    }

    function cancelTransition() {
      animation?.cancel(); animation = null;
      if (incoming !== null) scenes[incoming].classList.remove('is-entering');
      incoming = null; switching = false; root.dataset.transition = 'none';
    }

    function coverFailure() {
      if (destroyed) return;
      // Keep the current visible scene if the next cover cannot be decoded.
      serial++; paused = true; cancelTransition();
      root.dataset.motionError = 'A cover could not load; current scene retained';
      sync();
    }

    function sync() {
      const syncToken = ++syncSerial;
      clearTimeout(timer);
      if (destroyed) return;
      const active = running();
      root.classList.toggle('is-running', active);
      root.dataset.motionState = paused ? 'paused' : active ? 'playing' : 'suspended';
      toggle.setAttribute('aria-label', paused ? 'Play banner motion' : 'Pause banner motion');
      toggle.querySelector('[data-scene-toggle-label]').textContent = paused ? 'Play' : 'Pause';
      toggle.querySelector('[data-scene-play]').toggleAttribute('hidden', !paused);
      toggle.querySelector('[data-scene-pause]').toggleAttribute('hidden', paused);
      if (!active) {
        animation?.pause(); videos.forEach(video => video?.pause()); return;
      }
      if (animation) {animation.play(); return}
      if (switching) return;
      const selected = current;
      // Start the active video as soon as its first frame is ready. The poster is
      // already in the document and must not delay muted autoplay.
      const activeReady = videos[selected] ? prepareVideo(selected) : preparePoster(selected);
      activeReady.then(() => {
        if (!running() || current !== selected || switching || syncToken !== syncSerial) return;
        const video = videos[selected];
        if (video && !bad[selected]) {
          if (video.ended) {
            if (scenes.length > 1) {change(nextIndex(), false); return}
            video.currentTime = 0;
          }
          video.play().catch(error => {
            if (destroyed || current !== selected || error.name === 'AbortError') return;
            if (error.name === 'NotAllowedError') {
              paused = true; root.dataset.motionError = 'Press Play to start video';
            } else videoFallback(selected);
            sync();
          });
        } else if (scenes.length > 1) timer = setTimeout(() => change(nextIndex(), false), hold);
        // Protect the playing video from bandwidth competition. The next scene
        // starts loading only after the current scene has a safe buffer.
        warmNextWhenSafe(selected, syncToken);
      }).catch(coverFailure);
    }

    async function change(next, manual) {
      if (destroyed || next < 0 || next >= scenes.length) return;
      if (manual) {paused = true; serial++; cancelTransition(); sync()}
      if (next === current || switching) return;
      const token = ++serial;
      switching = true; clearTimeout(timer); clearTimeout(warmTimer);
      try {
        if (manual) await preparePoster(next); else await prepare(next);
        if (destroyed || token !== serial) return;
        if (!manual && !running()) {switching = false; return}
        const previous = current;
        function commit() {
          if (destroyed || token !== serial) return;
          scenes[next].classList.add('is-active'); scenes[next].classList.remove('is-entering');
          scenes[previous].classList.remove('is-active');
          const finished = animation;
          current = next; incoming = null; switching = false; animation = null;
          finished?.cancel(); resetVideo(previous); selection(next);
          root.dataset.transition = 'none'; root.dataset.sceneChanges = String(++cycles);
          sync();
        }
        if (manual || preference.matches || !scenes[next].animate) {commit(); return}
        videos.forEach(video => video?.pause());
        incoming = next; scenes[next].classList.add('is-entering');
        root.dataset.transition = String(previous + 1) + '-' + String(next + 1);
        // Fade stationary boundary frames, then start the next performance.
        // The outgoing scene stays opaque underneath: no white wipe.
        animation = scenes[next].animate([{opacity: 0}, {opacity: 1}], {
          duration: fade, easing: 'cubic-bezier(.4,0,.2,1)', fill: 'forwards'
        });
        animation.finished.then(commit).catch(() => {});
      } catch {if (token === serial) coverFailure()}
    }

    videos.forEach((video, index) => {
      if (!video) return;
      listen(video, 'ended', () => {
        if (index !== current || switching || !running()) return;
        if (scenes.length > 1) change(nextIndex(), false); else {video.currentTime = 0; sync()}
      });
      listen(video, 'error', () => {
        if (bad[index] || destroyed) return;
        videoFallback(index);
        if (index === current && !switching) sync();
      });
    });
    listen(toggle, 'click', () => {paused = !paused; sync()});
    dots.forEach((dot, index) => listen(dot, 'click', () => change(index, true)));
    listen(document, 'visibilitychange', sync);
    listen(preference, 'change', () => {if (preference.matches) paused = true; sync()});
    const observer = 'IntersectionObserver' in window ? new IntersectionObserver(entries => {
      visible = entries[0].isIntersecting; sync();
    }, {threshold: .12}) : null;
    root.dataset.sceneChanges = '0'; root.dataset.transition = 'none'; selection(0);
    controls.hidden = scenes.length === 1 && !videos[0];
    instances.set(root, () => {
      destroyed = true; serial++; clearTimeout(timer); clearTimeout(warmTimer); cancelTransition();
      observer?.disconnect(); abort.abort();
      videos.forEach(video => {
        if (!video) return;
        video.pause();
        video.querySelectorAll('source').forEach(source => source.removeAttribute('src'));
        video.load();
      });
      instances.delete(root);
    });
    preparePoster(0).catch(coverFailure);
    observer?.observe(root); sync();
  }
  const init = scope => scope.querySelectorAll('[data-banner-motion]').forEach(setup);
  init(document);
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => {
    instances.forEach((destroy, root) => {if (event.target.contains(root)) destroy()});
  });
})();




